import { useConsent } from "@/context/ConsentContext";
import { Button } from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { Cookie } from "lucide-react";
import { useEffect } from "react";
import { cn } from "@/utils";
import { shouldShowConsentBanner } from "@/utils/consent";

/** `embedded` renders the banner in place, e.g. inside the full-screen onboarding. */
export function ConsentBanner({ embedded = false }: { embedded?: boolean }) {
  const {
    hydrated,
    decided,
    impersonated,
    preferencesOpen,
    isBannerEmbedded,
    setBannerEmbedded,
    acceptAll,
    rejectAll,
    openPreferences,
    statement,
  } = useConsent();

  useEffect(() => {
    if (!embedded) return;

    setBannerEmbedded(true);
    return () => setBannerEmbedded(false);
  }, [embedded, setBannerEmbedded]);

  // Held back until the statement arrives rather than shown with a blank
  // message: this collects a decision, and one taken against wording nobody
  // saw is not consent. The embedded variant carries its own short copy.
  if (!embedded && statement === null) return null;

  if (
    !shouldShowConsentBanner({
      hydrated,
      decided,
      impersonated,
      preferencesOpen,
      embedded,
      isBannerEmbedded,
    })
  ) {
    return null;
  }

  return (
    <div
      className={cn(
        "rounded-2xl border border-divider",
        embedded
          ? "bg-content1 p-3"
          : "fixed right-[calc(1rem_+_var(--safe-area-right,0px))] bottom-[calc(1rem_+_var(--safe-area-bottom,0px))] z-40 w-[calc(100%-2rem)] max-w-sm bg-background/95 p-4 shadow-2xl backdrop-blur-md",
      )}
      data-identifier="consent-banner"
      role="region"
      aria-labelledby="consent-banner-message"
    >
      <div className="flex flex-col gap-3">
        {embedded ? (
          // Short so it does not stall sign-up; the full wording is in Nastavitve.
          <div className="flex items-start gap-3">
            <Cookie size={20} className="mt-0.5 shrink-0 text-primary" />
            <p id="consent-banner-message" className="text-sm">
              <Trans>
                Piškotke uporabljamo za analitiko in prilagojene oglase. Izbiro
                lahko kadarkoli spremenite.
              </Trans>
            </p>
          </div>
        ) : (
          <p
            id="consent-banner-message"
            className="text-foreground-600 text-sm"
          >
            {statement}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="light"
            size="sm"
            onPress={openPreferences}
            data-identifier="consent-manage"
          >
            <Trans>Nastavitve</Trans>
          </Button>
          <Button
            variant="bordered"
            size="sm"
            onPress={rejectAll}
            data-identifier="consent-reject"
          >
            <Trans>Zavrni</Trans>
          </Button>
          <Button
            color="primary"
            size="sm"
            onPress={acceptAll}
            data-identifier="consent-accept"
          >
            <Trans>Sprejmi</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}
